import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Plus, Pencil, Trash2, MapPin, Handshake } from "lucide-react";
import { toast } from "sonner";
import QuickSearchBar, { useQuickFilter } from "../components/QuickSearchBar";
import ExportCsvButton from "../components/ExportCsvButton";
import StickyPageHeader from "../components/StickyPageHeader";
import BeneficiaryCombobox from "../components/BeneficiaryCombobox";

const TYPES = [
  { value: "teletravail", label: "Télétravail" },
  { value: "plateforme_en_ligne", label: "Plateforme en ligne" },
  { value: "terrain_sites", label: "Terrain (sites entreprise)" },
  { value: "terrain", label: "Terrain (hors sites entreprise)" },
  { value: "bureau", label: "Bureau" },
  { value: "chantier", label: "Chantier" },
  { value: "agence", label: "Agence" },
  { value: "pdv", label: "Point de vente" },
  { value: "autre", label: "Autre" },
];

// Types de sites sans géo-fencing : le pointage n'y déclenche aucun contrôle
// de zone (télétravail = à distance ; plateforme en ligne = travail sur une
// plateforme distante ; terrain = intervention sur le terrain, que ce soit
// sur un site entreprise ou en dehors).
const NON_GEO_TYPES = new Set(["teletravail", "plateforme_en_ligne", "terrain", "terrain_sites"]);
const isNonGeoType = (t) => NON_GEO_TYPES.has(t);

const COLORS = ["#0055FF", "#059669", "#D97706", "#DC2626", "#7C3AED", "#0EA5E9", "#EC4899", "#71717A"];

const EMPTY_FORM = {
  name: "",
  type: "bureau",
  brand_ids: [],
  color: "#0055FF",
  address: "",
  description: "",
  latitude: "",
  longitude: "",
  radius_m: "",
  geofencing_enabled: true,
  commission_amount: "",
  partner_beneficiary_id: "",
};

export default function Sites() {
  const { user } = useAuth();
  const { brands, selectedBrandId } = useBrands();
  const [list, setList] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [filters, setFilters] = useState({ q: "", brand_id: "", type: "" });

  const canManage = user?.role === "admin" || user?.role === "manager";
  // Le Gestionnaire peut créer un site (limité à ses départements côté API)
  // mais ne peut ni modifier ni supprimer un site existant.
  const canCreate = canManage || user?.role === "gestionnaire";

  const load = async () => {
    try {
      const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
      const { data } = await api.get("/sites", { params });
      setList(data || []);
    } catch {
      setList([]);
    }
  };

  const loadBeneficiaries = async () => {
    try {
      const { data } = await api.get("/beneficiaries");
      setBeneficiaries(data || []);
    } catch {
      setBeneficiaries([]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selectedBrandId]);
  useEffect(() => { loadBeneficiaries(); }, []);

  const openCreate = () => {
    setEditing(null);
    // Par défaut, on pré-coche le premier département pour faciliter la saisie (admin peut tout cocher).
    const defaults = brands.length === 1 ? [brands[0].id] : [];
    setForm({ ...EMPTY_FORM, brand_ids: defaults });
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    // Rétro-compat : si le site est legacy (brand_id seul), on hydrate brand_ids depuis brand_id.
    const ids = Array.isArray(s.brand_ids) && s.brand_ids.length > 0
      ? s.brand_ids
      : (s.brand_id ? [s.brand_id] : []);
    setForm({
      name: s.name || "",
      type: s.type || "bureau",
      brand_ids: ids,
      color: s.color || "#0055FF",
      address: s.address || "",
      description: s.description || "",
      latitude: s.latitude != null ? String(s.latitude) : "",
      longitude: s.longitude != null ? String(s.longitude) : "",
      radius_m: s.radius_m != null ? String(s.radius_m) : "",
      geofencing_enabled: s.geofencing_enabled !== false, // défaut true (rétro-compat legacy)
      commission_amount: s.commission_amount != null ? String(s.commission_amount) : "",
      partner_beneficiary_id: s.partner_beneficiary_id || "",
    });
    setOpen(true);
  };

  const toggleBrand = (bid) => {
    setForm((f) => ({
      ...f,
      brand_ids: (f.brand_ids || []).includes(bid)
        ? f.brand_ids.filter((x) => x !== bid)
        : [...(f.brand_ids || []), bid],
    }));
  };

  const useCurrentPosition = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Géo-localisation indisponible");
      return;
    }
    toast.info("Récupération de votre position…");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({
          ...f,
          latitude: pos.coords.latitude.toFixed(6),
          longitude: pos.coords.longitude.toFixed(6),
        }));
        toast.success("Position renseignée");
      },
      () => toast.error("Position refusée ou indisponible"),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Nom requis");
      return;
    }
    if (!form.brand_ids || form.brand_ids.length === 0) {
      toast.error("Sélectionnez au moins un département");
      return;
    }
    try {
      const isCommissionable = form.type === "agence" || form.type === "pdv";
      const payload = {
        ...form,
        latitude: form.latitude !== "" ? parseFloat(form.latitude) : null,
        longitude: form.longitude !== "" ? parseFloat(form.longitude) : null,
        radius_m: form.radius_m !== "" ? parseInt(form.radius_m, 10) : null,
        geofencing_enabled: !!form.geofencing_enabled,
        commission_amount: isCommissionable && form.commission_amount !== "" && form.commission_amount !== null
          ? parseFloat(form.commission_amount)
          : null,
        partner_beneficiary_id: isCommissionable && form.partner_beneficiary_id
          ? form.partner_beneficiary_id
          : null,
      };
      if (editing) {
        await api.put(`/sites/${editing.id}`, payload);
        toast.success("Site mis à jour");
      } else {
        await api.post("/sites", payload);
        toast.success("Site créé");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce site ?")) return;
    try {
      await api.delete(`/sites/${id}`);
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const filteredList = useQuickFilter(list, filters, {
    text: (s) => `${s.name || ""} ${s.address || ""} ${s.description || ""}`,
    predicates: {
      brand_id: (s, value) => {
        const ids = s.brand_ids && s.brand_ids.length ? s.brand_ids : (s.brand_id ? [s.brand_id] : []);
        return ids.includes(value);
      },
      type: (s, value) => s.type === value,
    },
  });

  return (
    <div className="space-y-6" data-testid="sites-page">
      {/* Dialog déclaré hors du StickyPageHeader pour éviter qu'il ne soit démonté
          quand l'utilisateur replie l'en-tête (bug : le crayon d'édition n'ouvrait
          plus la modale une fois le header réduit). */}
      {canCreate && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
              <DialogHeader>
                <DialogTitle>{editing ? "Modifier le site" : "Nouveau site"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="label-uppercase mb-1.5 block">Nom du site</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Bureau Paris 9e" data-testid="site-name" />
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger data-testid="site-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TYPES.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`site-type-opt-${t.value}`}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Départements rattachés</Label>
                    <div className="flex flex-wrap gap-2" data-testid="site-brands-multi">
                      {brands.map((b) => {
                        const active = (form.brand_ids || []).includes(b.id);
                        return (
                          <button
                            key={b.id}
                            type="button"
                            onClick={() => toggleBrand(b.id)}
                            className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${active ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 bg-white text-zinc-700 hover:border-zinc-400"}`}
                            data-testid={`site-brand-opt-${b.id}`}
                          >
                            <span className="inline-block h-2 w-2 rounded-full mr-1.5 align-middle" style={{ backgroundColor: b.color }} />
                            {b.name}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-zinc-500 font-mono mt-1.5">
                      Un site peut être affecté à plusieurs départements. Chaque manager ne voit que les sites de ses départements.
                    </p>
                  </div>
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Adresse</Label>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Adresse complète" data-testid="site-address" />
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="site-description" />
                </div>

                {!isNonGeoType(form.type) && (
                  <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50/50 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="label-uppercase">Géo-fencing</p>
                        <p className="text-[11px] text-zinc-500 font-mono mt-0.5">
                          Définit la zone autorisée pour le pointage sur ce site
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={useCurrentPosition}
                        className="rounded-sm"
                        disabled={!form.geofencing_enabled}
                        data-testid="site-use-position"
                      >
                        <MapPin size={12} className="mr-1" /> Ma position
                      </Button>
                    </div>
                    {/* Interrupteur pour activer / désactiver le contrôle de zone */}
                    <div
                      className="flex items-center justify-between gap-3 bg-white border border-zinc-200 rounded-sm px-3 py-2"
                      data-testid="site-geofencing-toggle-row"
                    >
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-zinc-900">
                          {form.geofencing_enabled ? "Contrôle de zone activé" : "Contrôle de zone désactivé"}
                        </p>
                        <p className="text-[10px] text-zinc-500 font-mono">
                          {form.geofencing_enabled
                            ? "Les pointages hors du rayon seront marqués « hors zone »."
                            : "Les pointages ne seront jamais marqués « hors zone » sur ce site."}
                        </p>
                      </div>
                      <Switch
                        checked={!!form.geofencing_enabled}
                        onCheckedChange={(v) => setForm({ ...form, geofencing_enabled: !!v })}
                        data-testid="site-geofencing-toggle"
                      />
                    </div>
                    <div className={`grid grid-cols-3 gap-3 ${form.geofencing_enabled ? "" : "opacity-50 pointer-events-none"}`}>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-600 mb-1 block">Latitude</Label>
                        <Input
                          type="number"
                          step="0.000001"
                          value={form.latitude}
                          onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                          placeholder="48.8566"
                          data-testid="site-lat"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-600 mb-1 block">Longitude</Label>
                        <Input
                          type="number"
                          step="0.000001"
                          value={form.longitude}
                          onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                          placeholder="2.3522"
                          data-testid="site-lon"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-600 mb-1 block">Rayon (m)</Label>
                        <Input
                          type="number"
                          min="10"
                          value={form.radius_m}
                          onChange={(e) => setForm({ ...form, radius_m: e.target.value })}
                          placeholder="200"
                          data-testid="site-radius"
                        />
                      </div>
                    </div>
                    <p className="text-[10px] text-zinc-500 font-mono">
                      Par défaut : 200 m. Laissez vide pour désactiver le contrôle de zone.
                    </p>
                  </div>
                )}
                {form.type === "teletravail" && (
                  <p className="text-[11px] text-zinc-500 font-mono italic">
                    Les sites de type Télétravail ne sont pas géo-localisés — les pointages associés sont automatiquement marqués <span className="font-semibold">Télétravail</span>.
                  </p>
                )}
                {form.type === "plateforme_en_ligne" && (
                  <p className="text-[11px] text-zinc-500 font-mono italic">
                    Les sites de type Plateforme en ligne ne sont pas géo-localisés — les pointages associés sont automatiquement marqués <span className="font-semibold">Plateforme en ligne</span>.
                  </p>
                )}
                {form.type === "terrain_sites" && (
                  <p className="text-[11px] text-zinc-500 font-mono italic">
                    Les sites de type Terrain (sites entreprise) ne sont pas géo-localisés — aucun contrôle de zone n&apos;est appliqué au pointage, celui-ci est automatiquement marqué <span className="font-semibold">Terrain</span>.
                  </p>
                )}
                {form.type === "terrain" && (
                  <p className="text-[11px] text-zinc-500 font-mono italic">
                    Les sites de type Terrain (hors sites entreprise) ne sont pas géo-localisés — aucun contrôle de zone n&apos;est appliqué au pointage, celui-ci est automatiquement marqué <span className="font-semibold">Terrain</span>.
                  </p>
                )}
                {(form.type === "agence" || form.type === "pdv") && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 space-y-2" data-testid="site-commission-block">
                    <Label className="label-uppercase text-emerald-900 flex items-center gap-1.5">
                      Prime partenaire HT
                      {user?.role !== "admin" && (
                        <span className="text-[9px] font-mono text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-sm normal-case">Lecture seule</span>
                      )}
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Ex : 30.00 (défaut département)"
                        value={form.commission_amount}
                        onChange={(e) => setForm({ ...form, commission_amount: e.target.value })}
                        disabled={user?.role !== "admin"}
                        data-testid="site-commission-amount"
                      />
                      <span className="text-sm font-mono text-emerald-800">€ HT / vente</span>
                    </div>
                    <p className="text-[10px] font-mono text-emerald-800 leading-relaxed">
                      Montant crédité à ce site pour chaque vente réalisée.<br />
                      Laisser vide pour utiliser le montant par défaut du département (fallback 30 € HT).<br />
                      Toute modification déclenche un recalcul rétroactif des commissions non éditées manuellement.
                    </p>
                  </div>
                )}
                {(form.type === "agence" || form.type === "pdv") && (
                  <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 space-y-2" data-testid="site-partner-block">
                    <Label className="label-uppercase text-blue-900 flex items-center gap-1.5">
                      <Handshake size={12} /> Contact partenaire associé
                      <span className="text-[9px] font-mono text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded-sm normal-case">optionnel</span>
                    </Label>
                    <BeneficiaryCombobox
                      beneficiaries={beneficiaries}
                      categoryName="Partenaire"
                      value={form.partner_beneficiary_id}
                      onChange={(v) => setForm({ ...form, partner_beneficiary_id: v || "" })}
                      placeholder="Choisir un contact partenaire…"
                      allowClear
                      data-testid="site-partner-combobox"
                    />
                    <p className="text-[10px] font-mono text-blue-800 leading-relaxed">
                      Une fois associé, toutes les transactions générées sur ce site apparaîtront
                      dans l&apos;onglet « Historique des transactions » du dossier du contact partenaire.
                    </p>
                  </div>
                )}
                <div>
                  <Label className="label-uppercase mb-1.5 block">Couleur d&apos;identification</Label>
                  <div className="flex gap-2 flex-wrap">
                    {COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setForm({ ...form, color: c })}
                        className={`h-9 w-9 rounded-sm border-2 ${form.color === c ? "border-zinc-900" : "border-transparent"}`}
                        style={{ backgroundColor: c }}
                        data-testid={`site-color-${c}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button onClick={save} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="site-save-btn">Enregistrer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
      )}

      <StickyPageHeader testid="sites-sticky-header">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-uppercase mb-2">Lieux de travail</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900">
            Sites
          </h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">
            Télétravail, bureaux, chantiers, agences, points de vente…
          </p>
        </div>
        {canCreate && (
          <Button onClick={openCreate} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-sm" data-testid="new-site-btn">
            <Plus size={14} className="mr-1" /> Nouveau site
          </Button>
        )}
      </div>

      <QuickSearchBar
        value={filters}
        onChange={setFilters}
        placeholder="Rechercher un site (nom, adresse, description)…"
        testid="sites-search"
        filters={[
          { key: "brand_id", label: "Département", type: "select", placeholder: "Tous les départements", options: brands.map((b) => ({ value: b.id, label: b.name })) },
          { key: "type", label: "Type", type: "select", placeholder: "Tous les types", options: TYPES.map((t) => ({ value: t.value, label: t.label })) },
        ]}
      />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[11px] text-zinc-500 font-mono" data-testid="sites-result-count">
          {filteredList.length} résultat{filteredList.length > 1 ? "s" : ""} sur {list.length}
        </p>
        <ExportCsvButton
          filename="sites"
          testid="sites-export-csv"
          rows={filteredList}
          columns={[
            { label: "Nom", value: (s) => s.name },
            { label: "Type", value: (s) => (TYPES.find((t) => t.value === s.type)?.label || s.type) },
            { label: "Adresse", value: (s) => s.address || "" },
            { label: "Départements", value: (s) => {
              const ids = s.brand_ids && s.brand_ids.length ? s.brand_ids : (s.brand_id ? [s.brand_id] : []);
              return ids.map((id) => brands.find((b) => b.id === id)?.name).filter(Boolean).join(", ");
            } },
            { label: "Description", value: (s) => s.description || "" },
            { label: "Latitude", value: (s) => (s.latitude != null ? s.latitude : "") },
            { label: "Longitude", value: (s) => (s.longitude != null ? s.longitude : "") },
            { label: "Rayon (m)", value: (s) => (s.radius_m != null ? s.radius_m : "") },
            { label: "Prime partenaire HT", value: (s) => (s.commission_amount != null ? s.commission_amount : "") },
            { label: "Partenaire associé", value: (s) => beneficiaries.find((b) => b.id === s.partner_beneficiary_id)?.denomination || "" },
          ]}
        />
      </div>
      </StickyPageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredList.map((s) => {
          const siteBrandIds = s.brand_ids && s.brand_ids.length ? s.brand_ids : (s.brand_id ? [s.brand_id] : []);
          const siteBrands = brands.filter((x) => siteBrandIds.includes(x.id));
          const t = TYPES.find((x) => x.value === s.type);
          return (
            <div key={s.id} className="bg-white border border-zinc-200 rounded-sm p-5 hover:border-zinc-300" data-testid={`site-${s.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="h-9 w-9 rounded-sm flex items-center justify-center" style={{ backgroundColor: `${s.color}20`, color: s.color }}>
                    <MapPin size={16} />
                  </span>
                  <span className="text-[10px] uppercase font-semibold tracking-wider px-2 py-0.5 bg-zinc-50 border border-zinc-200 rounded-sm text-zinc-700">
                    {t?.label || s.type}
                  </span>
                </div>
                {canManage && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-site-${s.id}`}>
                      <Pencil size={12} />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(s.id)} data-testid={`delete-site-${s.id}`}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                )}
              </div>
              <h3 className="font-display text-lg font-semibold text-zinc-900">{s.name}</h3>
              {s.address && <p className="text-xs text-zinc-600 mt-1 font-mono">{s.address}</p>}
              {s.description && <p className="text-sm text-zinc-600 mt-2 line-clamp-2">{s.description}</p>}
              {!isNonGeoType(s.type) && s.latitude != null && s.longitude != null && (
                <p className="text-[11px] text-zinc-500 mt-2 font-mono inline-flex items-center gap-1" data-testid={`site-geofence-${s.id}`}>
                  <MapPin size={10} /> Zone : {s.radius_m || 200} m · {Number(s.latitude).toFixed(4)}, {Number(s.longitude).toFixed(4)}
                </p>
              )}
              {!isNonGeoType(s.type) && (
                <p
                  className={`mt-1 text-[10px] font-mono uppercase tracking-wider inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border ${
                    s.geofencing_enabled === false
                      ? "bg-zinc-100 border-zinc-200 text-zinc-500"
                      : "bg-emerald-50 border-emerald-200 text-emerald-800"
                  }`}
                  data-testid={`site-geofencing-status-${s.id}`}
                >
                  Géo-fencing : {s.geofencing_enabled === false ? "désactivé" : "activé"}
                </p>
              )}
              {(s.type === "agence" || s.type === "pdv") && (
                <p className="text-[11px] text-emerald-800 mt-2 font-mono inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded-sm px-1.5 py-0.5" data-testid={`site-commission-${s.id}`}>
                  Prime : {s.commission_amount != null
                    ? `${Number(s.commission_amount).toFixed(2).replace(".", ",")} € HT`
                    : "défaut département"}
                </p>
              )}
              {(s.type === "agence" || s.type === "pdv") && s.partner_beneficiary_id && (
                <p
                  className="text-[11px] text-blue-800 mt-2 ml-1.5 font-mono inline-flex items-center gap-1 bg-blue-50 border border-blue-200 rounded-sm px-1.5 py-0.5"
                  data-testid={`site-partner-${s.id}`}
                >
                  <Handshake size={10} />
                  {beneficiaries.find((b) => b.id === s.partner_beneficiary_id)?.denomination || "Partenaire lié"}
                </p>
              )}
              {siteBrands.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {siteBrands.map((b) => (
                    <span key={b.id} className="inline-flex items-center gap-1.5 text-[11px] font-mono text-zinc-700 px-2 py-0.5 bg-zinc-50 border border-zinc-200 rounded-sm">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: b.color }} />
                      {b.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {filteredList.length === 0 && (
          <div className="col-span-3 text-center py-12 text-sm text-zinc-500 font-mono bg-white border border-zinc-200 rounded-sm">
            {list.length === 0 ? "Aucun site enregistré" : "Aucun site ne correspond à votre recherche"}
          </div>
        )}
      </div>
    </div>
  );
}
